import React, { type FC, memo, type ReactNode, useCallback, useMemo } from "react";
import { Button, cn, Dropdown, type Key, Label } from "@heroui/react";
import { ChevronDown } from "@gravity-ui/icons";
import { matchPath, type NavigateFunction, useNavigate } from "react-router-dom";

export interface NavItem {
    key: string;
    label: ReactNode;
    to?: string;
    onClick?: () => void;
    disabled?: boolean;
    children?: NavItem[];
    // exact match only: for a module index route whose key would otherwise prefix-match every child route
    end?: boolean;
}

interface Props {
    items: NavItem[];
    selectedKeys?: string[];
}

const navItemClass =
    "h-8 px-3 text-sm rounded-md cursor-pointer flex items-center text-inherit hover:bg-black/5 transition-colors";
const activeClass = "font-bold";

const isItemActive = (item: NavItem, keys: string[]): boolean => {
    if (item.children?.length) {
        return item.children.some((child) => isItemActive(child, keys));
    }
    return keys.some((key) => {
        if (key === item.key) {
            return true;
        }
        try {
            return !!matchPath({ path: item.key, end: item.end ?? false }, key);
        } catch {
            return false;
        }
    });
};

const NavDropdown: FC<{ item: NavItem; isActive: boolean; navigate: NavigateFunction }> = memo(
    ({ item, isActive, navigate }) => {
        const disabledKeys = useMemo(
            () => item.children?.filter((c) => c.disabled).map((c) => c.key) ?? [],
            [item.children],
        );
        const handleAction = useCallback(
            (id: Key) => {
                const child = item.children?.find((c) => c.key === String(id));
                if (child?.to) {
                    navigate(child.to);
                } else {
                    child?.onClick?.();
                }
            },
            [item.children, navigate],
        );
        return (
            <Dropdown>
                <Dropdown.Trigger>
                    <Button
                        variant="ghost"
                        size="sm"
                        className={cn(navItemClass, isActive && activeClass, "text-inherit")}
                    >
                        {item.label}
                        <ChevronDown className="size-4" />
                    </Button>
                </Dropdown.Trigger>
                <Dropdown.Popover>
                    <Dropdown.Menu
                        aria-label={String(item.key)}
                        className="Menu--MaxHeight"
                        disabledKeys={disabledKeys}
                        onAction={handleAction}
                    >
                        {(item.children ?? []).map((child) => (
                            <Dropdown.Item key={child.key} id={child.key} textValue={String(child.label)}>
                                <Label>{child.label}</Label>
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown.Popover>
            </Dropdown>
        );
    },
);

const NavItemButton: FC<{ item: NavItem; isActive: boolean; navigate: NavigateFunction }> = memo(
    ({ item, isActive, navigate }) => {
        const handlePress = useCallback(() => {
            if (item.to) {
                navigate(item.to);
            }
            item.onClick?.();
        }, [item, navigate]);
        return (
            <Button
                variant="ghost"
                size="sm"
                isDisabled={item.disabled}
                onPress={handlePress}
                className={cn(navItemClass, "text-inherit", isActive && activeClass)}
            >
                {item.label}
            </Button>
        );
    },
);

const ModuleNavFC: FC<Props> = ({ items, selectedKeys = [] }) => {
    const navigate = useNavigate();
    return (
        <nav className="flex items-center h-full">
            {items.map((item) => {
                const isActive = isItemActive(item, selectedKeys);

                if (item.children?.length) {
                    return <NavDropdown key={item.key} item={item} isActive={isActive} navigate={navigate} />;
                }

                if (item.onClick || item.to) {
                    return <NavItemButton key={item.key} item={item} isActive={isActive} navigate={navigate} />;
                }

                return (
                    <span
                        key={item.key}
                        className={cn(navItemClass, "text-inherit", isActive && activeClass, {
                            "opacity-50 pointer-events-none": item.disabled,
                            "hover:bg-transparent": item.key === "selector",
                        })}
                    >
                        {item.label}
                    </span>
                );
            })}
        </nav>
    );
};

export const ModuleNav = memo(ModuleNavFC);
